import { memo, useCallback, useEffect, useMemo } from "react";
import { clsx } from "clsx";

import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { useAppSelector } from "@/shared/lib/hooks/redux";
import { ActionBtn } from "@/shared/ui/Btn";
import { MostPopular } from "@/shared/ui/MostPopular";
import { Spinner } from "@/shared/ui/Spinner";

import WhiteCheckIcon from "@/shared/assets/icons/whiteCheck.svg";

import { ProductInterval } from "../../const/subscription";
import { getBaseProductName } from "../../lib/getBaseProductName";
import { getProductsAreLoading, getProductsList } from "../../model/selectors/subscriptionSelectors";
import type { ProductData } from "../../model/types/productData";

import cls from "./PlanSelection.module.scss";

const MOST_POPULAR_PLAN_NAME = "Flame";

type PlanSelectionProps = {
  className?: string;
  variant?: "dark" | "light";
  title?: string;
  submitText?: string;
  isBusy?: boolean;
  stripePriceId?: string | null;
  /** Preselected tier, matched by name so an annual price resolves to its monthly analog. */
  preferredStripePriceId?: string | null;
  onChange: (stripePriceId: string) => void;
  onSubmit: () => void;
};

export const PlanSelection = memo((props: PlanSelectionProps) => {
  const {
    className,
    variant = "dark",
    title,
    submitText = "CONTINUE",
    isBusy,
    stripePriceId,
    preferredStripePriceId,
    onChange,
    onSubmit,
  } = props;

  const { showAlert } = useAlert();

  const productsAreLoading = useAppSelector(getProductsAreLoading);
  const productsList = useAppSelector(getProductsList);

  const monthlyProducts = useMemo(() => {
    return productsList
      .filter((productData) => productData.interval === ProductInterval.Monthly)
      .sort((a, b) => Number(a.price) - Number(b.price));
  }, [productsList]);

  useEffect(() => {
    if (stripePriceId || !monthlyProducts.length) {
      return;
    }

    const preferredName = getBaseProductName(
      productsList.find((productData) => productData.stripePriceId === preferredStripePriceId)?.name
    );

    const defaultProduct =
      (preferredName &&
        monthlyProducts.find((productData) => getBaseProductName(productData.name) === preferredName)) ||
      monthlyProducts.find((productData) => productData.name === MOST_POPULAR_PLAN_NAME);

    if (defaultProduct) {
      onChange(defaultProduct.stripePriceId);
    }
  }, [monthlyProducts, onChange, preferredStripePriceId, productsList, stripePriceId]);

  const onClickSubmit = useCallback(() => {
    if (!stripePriceId) {
      showAlert({ theme: AlertTheme.ERROR, message: "Choose your plan" } as AlertOptions);
      return;
    }

    onSubmit();
  }, [onSubmit, showAlert, stripePriceId]);

  const renderItem = (productData: ProductData) => {
    const isSelected = stripePriceId === productData.stripePriceId;

    return (
      <div
        key={productData.stripePriceId}
        className={clsx(cls.Product, isSelected && cls.isSelected)}
        onClick={() => onChange(productData.stripePriceId)}
      >
        {productData.name === MOST_POPULAR_PLAN_NAME && <MostPopular position="left" variant="dark" />}
        <div className={cls.LeftSide}>
          <div className={cls.Name}>{productData.name ?? "None"}</div>
          <div className={cls.Info}>
            <p>Unlimited readings</p>
            <p>
              {productData.maxPersonalityChanges === 999 ? "Unlimited" : productData.maxPersonalityChanges}{" "}
              {productData.maxPersonalityChanges === 1 ? "psychic" : "psychics"}
            </p>
          </div>
        </div>
        <div className={cls.RightSide}>
          <div className={cls.Price}>${Number(productData.price).toFixed(0)}</div>
          <div className={cls.Interval}>/month</div>
        </div>
        {isSelected && <WhiteCheckIcon className={cls.CheckIcon} />}
      </div>
    );
  };

  return (
    <div className={clsx(cls.Container, cls[variant], className)}>
      {title && <div className={cls.Title}>{title}</div>}
      {productsAreLoading ? <Spinner /> : <div className={cls.Options}>{monthlyProducts.map(renderItem)}</div>}
      <div className={cls.ButtonContainer}>
        <ActionBtn disabled={!stripePriceId} isBusy={isBusy} onClick={onClickSubmit}>
          {submitText}
        </ActionBtn>
      </div>
    </div>
  );
});
